// Admin landing page: the companies list alongside the Content Management
// cards. Demo/Settings card columns are omitted since those routes don't exist.
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';
import { createColumnHelper } from '@tanstack/react-table';
import Navbar from '../../components/Navbar';
import PageWrapper from '../../components/PageWrapper';
import PageWithAsyncData from '../../components/PageWithAsyncData';
import { LabelUppercase } from '../../components/elements/Text';
import { ROUTES } from '../../routing';
import { useGetCompanies } from '../../hooks/use-hydra/admin';
import { CompaniesResponse } from '../../api/admin';
import Table from '../../components/Table/CompactTable';
import {
  PageTitle,
  SectionLabel,
  Panel,
  Card,
  CardIcon,
  CardTextWrapper,
  CardTitle,
  CardSubtitle,
  EmptyStateText,
} from '../../components/Admin/AdminLayout';

type Company = CompaniesResponse['items'][number];

const DashboardGrid = styled.div`
  display: grid;
  grid-template-columns: 1.5fr 1fr;
  gap: 32px;
  margin-top: 36px;
  margin-bottom: 72px;
  min-height: 45vh;
  @media screen and (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const LeftColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const CardColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const FlexPanel = styled(Panel)`
  flex: 1;
`;

const PanelFooter = styled.div`
  background-color: ${({ theme }) => theme.colors.bgDefaultSecondary};
  border-top: 1px solid ${({ theme }) => theme.colors.bgActive};
  padding: 10px 16px;
  margin-top: auto;
`;

const FooterLabel = styled(LabelUppercase)`
  margin: 0;
  color: ${({ theme }) => theme.colors.textPlaceholder};
`;

const TableWrapper = styled.div`
  .table-scroll-wrapper {
    padding-bottom: 0;
  }
`;

const EmptyState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 48px 16px;
  flex: 1;
`;

const NameCell = styled.div`
  display: flex;
  flex-direction: column;
`;

const PrimaryName = styled.span`
  font-weight: 500;
  color: ${({ theme }) => theme.colors.textRegular};
`;

const SecondarySlug = styled.span`
  color: ${({ theme }) => theme.colors.textPlaceholder};
  font-size: 13px;
`;

const columnHelper = createColumnHelper<Company>();

const companiesColumns = [
  columnHelper.accessor('name', {
    header: 'Company',
    cell: ({ row }) => (
      <NameCell>
        <PrimaryName>{row.original.name}</PrimaryName>
        <SecondarySlug>{row.original.slug}</SecondarySlug>
      </NameCell>
    ),
  }),
  columnHelper.accessor('numLearners', {
    header: 'Learners',
  }),
];

const cmsCards = [
  {
    title: 'Assessment Tests',
    subtitle: 'Multi-scenario assessment tests',
    icon: 'fa-solid fa-clipboard-list',
    getPath: () => ROUTES.ADMIN.CMS_ASSESSMENT_TESTS.getPath(),
  },
  {
    title: 'Modules',
    subtitle: 'Multi-lesson content modules',
    icon: 'fa-solid fa-book-open',
    getPath: () => ROUTES.ADMIN.CMS_MODULES.getPath(),
  },
  {
    title: 'Projects',
    subtitle: 'Applied single-lesson projects',
    icon: 'fa-solid fa-laptop-code',
    getPath: () => ROUTES.ADMIN.CMS_PROJECTS.getPath(),
  },
];

export default function AdminHome() {
  const history = useHistory();
  const { data: companiesData, isLoading, error, refetch } = useGetCompanies();
  const companies = companiesData?.items ?? [];

  return (
    <PageWithAsyncData isLoading={isLoading} error={error} retry={refetch}>
      <Navbar breadCrumbs={[{ label: 'Admin Dashboard', url: null }]} />
      <PageWrapper>
        <PageTitle>Admin Dashboard</PageTitle>
        <DashboardGrid>
          <LeftColumn>
            <SectionLabel>Companies</SectionLabel>
            <FlexPanel>
              {companies.length > 0 ? (
                <>
                  <TableWrapper>
                    <Table
                      data={companies}
                      getRowId={(row) => row.slug}
                      columns={companiesColumns}
                      className="companies-table"
                      getRowProps={(row) => ({
                        onClick: () =>
                          history.push(
                            ROUTES.ADMIN.COMPANY.getPath({
                              companyId: row.original.id.toString(),
                            })
                          ),
                      })}
                    />
                  </TableWrapper>
                  <PanelFooter>
                    <FooterLabel>
                      {companies.length}{' '}
                      {companies.length === 1 ? 'Company' : 'Companies'}
                    </FooterLabel>
                  </PanelFooter>
                </>
              ) : (
                <EmptyState>
                  <EmptyStateText>No companies yet.</EmptyStateText>
                </EmptyState>
              )}
            </FlexPanel>
          </LeftColumn>

          <CardColumn>
            <SectionLabel>Content Management</SectionLabel>
            {cmsCards.map((card) => (
              <Card
                key={card.title}
                onClick={() => history.push(card.getPath())}
              >
                <CardIcon className={card.icon} />
                <CardTextWrapper>
                  <CardTitle>{card.title}</CardTitle>
                  <CardSubtitle>{card.subtitle}</CardSubtitle>
                </CardTextWrapper>
              </Card>
            ))}
          </CardColumn>
        </DashboardGrid>
      </PageWrapper>
    </PageWithAsyncData>
  );
}
